/**
 * Short, readable figures for the render result lists: how long a page took, how
 * big its pdf is, how much memory the renderer was using.
 */

export function formatBytes(bytes) {
  if (!Number.isFinite(bytes) || bytes < 0) {
    return '';
  }
  if (bytes < 1024 * 1024) {
    return `${Math.max(1, Math.round(bytes / 1024))} KB`;
  }
  const mb = bytes / (1024 * 1024);
  if (mb < 1024) {
    return `${mb < 10 ? mb.toFixed(1) : Math.round(mb)} MB`;
  }
  return `${(mb / 1024).toFixed(1)} GB`;
}

export function formatSeconds(ms) {
  if (!Number.isFinite(ms) || ms < 0) {
    return '';
  }
  const seconds = ms / 1000;
  if (seconds < 60) {
    return `${seconds < 10 ? seconds.toFixed(1) : Math.round(seconds)} s`;
  }
  const minutes = Math.floor(seconds / 60);
  return `${minutes}m ${Math.round(seconds - minutes * 60)}s`;
}

/** the js heap this browser is using, which only chrome reports; null elsewhere */
export function browserMemoryUsed() {
  const memory = typeof performance !== 'undefined' && performance.memory;
  return memory && Number.isFinite(memory.usedJSHeapSize) ? memory.usedJSHeapSize : null;
}

/** 'server 310 MB, chrome 85 MB' from [{label, bytes}], skipping unknown figures */
export function formatMemory(figures) {
  return (figures || [])
    .filter(f => Number.isFinite(f.bytes))
    .map(f => `${f.label} ${formatBytes(f.bytes)}`)
    .join(', ');
}

/**
 * One line for a rendered page: '4.2 s (loaded in 2.6 s) · 1.2 MB · server 310 MB, chrome 85 MB'.
 * Every part is optional; unknown figures are left out.
 */
export function pageStatsText({durationMs, waitedMs, size, memory} = {}) {
  const parts = [];
  if (Number.isFinite(durationMs)) {
    const waited = Number.isFinite(waitedMs) ? ` (loaded in ${formatSeconds(waitedMs)})` : '';
    parts.push(`${formatSeconds(durationMs)}${waited}`);
  }
  if (Number.isFinite(size)) {
    parts.push(formatBytes(size));
  }
  const memoryText = formatMemory(memory);
  if (memoryText) {
    parts.push(memoryText);
  }
  return parts.join(' · ');
}
